import apiClient from '@/scripts/api/apiClient';

/**
 * Organization API endpoints
 */

// Query key factories
export const organizationKeys = {
  getAllOrganizations: () => ['get', 'organizations'],
  getAllOrganizationsWithDetails: () => ['get', 'organizations', 'withDetails'],
  getOrganization: (organizationId) => ['get', 'organizations', organizationId],
  createOrganization: () => ['create', 'organizations'],
  updateOrganization: (organizationId) => ['update', 'organizations', organizationId],
  deleteOrganization: (organizationId) => ['delete', 'organizations', organizationId],
  getOrganizationChannels: (organizationId) => ['get', 'organizations', organizationId, 'channels'],
  getOrganizationMembers: (organizationId) => ['get', 'organizations', organizationId, 'members'],
  getOrganizationInvites: (organizationId) => ['get', 'organizations', organizationId, 'invites'],
  updateMemberRoles: (organizationId, userId) => ['update', 'organizations', organizationId, 'members', userId],
  removeOrganizationMember: (organizationId, userId) => ['delete', 'organizations', organizationId, 'members', userId],
  createOrganizationInvite: (organizationId) => ['create', 'organizations', organizationId, 'invites'],
  deleteOrganizationInvite: (organizationId, inviteId) => ['delete', 'organizations', organizationId, 'invites', inviteId],
};

/**
 * Get all organizations
 * @returns {Promise<Array>} List of organizations
 */
export const getAllOrganizations = async () => {
  const response = await apiClient.get('/api/organizations');
  return response.data;
};

/**
 * Get a specific organization by ID
 * @param {string} id - Organization ID
 * @returns {Promise<Object>} Organization details
 */
export const getOrganization = async (id) => {
  const response = await apiClient.get(`/api/organizations/${id}`);
  return response.data;
};

/**
 * Create a new organization
 * @param {Object} data - Organization data
 * @returns {Promise<Object>} Created organization
 */
export const createOrganization = async (data) => {
  const response = await apiClient.post('/api/organizations', data);
  return response.data;
};

/**
 * Update an organization
 * @param {string} id - Organization ID
 * @param {Object} data - Organization data to update
 * @returns {Promise<Object>} Updated organization
 */
export const updateOrganization = async (id, data) => {
  const response = await apiClient.put(`/api/organizations/${id}`, data);
  return response.data;
};

/**
 * Delete an organization
 * @param {string} id - Organization ID
 * @returns {Promise<Object>} Response data
 */
export const deleteOrganization = async (id) => {
  const response = await apiClient.delete(`/api/organizations/${id}`);
  return response.data;
};

/**
 * Get channels for a specific organization
 * @param {string} organizationId - Organization ID
 * @returns {Promise<Array>} List of channels belonging to the organization or empty array if no valid ID
 */
export const getOrganizationChannels = async (organizationId) => {
  if (!organizationId) return [];
  
  const response = await apiClient.get(`/api/organizations/${organizationId}/channels`);
  return response.data;
};

/**
 * Get members of a specific organization
 * @param {string} organizationId - Organization ID
 * @returns {Promise<Array>} List of organization members
 */
export const getOrganizationMembers = async (organizationId) => {
  const response = await apiClient.get(`/api/organizations/${organizationId}/members`);
  return response.data;
};

/**
 * Update member roles in an organization
 * @param {string} organizationId - Organization ID
 * @param {string} userId - User ID
 * @param {Array<string>} roles - Array of role names
 * @returns {Promise<Object>} Updated member details
 */
export const updateMemberRoles = async (organizationId, userId, roles) => {
  const response = await apiClient.put(`/api/organizations/${organizationId}/members/${userId}`, { roles });
  return response.data;
};

/**
 * Remove a member from an organization
 * @param {string} organizationId - Organization ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Response data
 */
export const removeOrganizationMember = async (organizationId, userId) => {
  const response = await apiClient.delete(`/api/organizations/${organizationId}/members/${userId}`);
  return response.data;
};

/**
 * Get invites for a specific organization
 * @param {string} organizationId - Organization ID
 * @returns {Promise<Array>} List of organization invites
 */
export const getOrganizationInvites = async (organizationId) => {
  const response = await apiClient.get(`/api/organizations/${organizationId}/invites`);
  return response.data;
};

/**
 * Invite a user to an organization
 * @param {string} organizationId - Organization ID
 * @param {Object} inviteData - Invite data
 * @returns {Promise<Object>} Created invite
 */
export const createOrganizationInvite = async (organizationId, inviteData) => {
  const response = await apiClient.post(`/api/organizations/${organizationId}/invites`, inviteData);
  return response.data;
};

/**
 * Delete an organization invite
 * @param {string} organizationId - Organization ID
 * @param {string} inviteId - Invite ID
 * @returns {Promise<Object>} Response data
 */
export const deleteOrganizationInvite = async (organizationId, inviteId) => {
  const response = await apiClient.delete(`/api/organizations/${organizationId}/invites/${inviteId}`);
  return response.data;
};

/**
 * Get organizations with additional computed details
 * @returns {Promise<Array>} List of organizations with computed properties
 */
export const getAllOrganizationsWithDetails = async () => {
  const organizations = await getAllOrganizations();

  return organizations.map(org => {
    // Find the owner (member with 'Owner' role)
    const owner = org.members?.find(member => 
      member.organization_roles?.includes('Owner')
    ) || null;
    
    // Get collaborators (all members except the owner)
    const collaborators = org.members?.filter(member => member.id !== owner?.id) || [];

    let fileCount = 0;
    let storage = 0;

    if (org.channels?.length > 0) {
      for (const channel of org.channels) {
        if (channel.video_files && Array.isArray(channel.video_files)) {
          fileCount += channel.video_files.length;
          for (const file of channel.video_files) {
            if (file.file_size && !isNaN(file.file_size)) {
              storage += Number(file.file_size);
            }
          }
        }
      }
    }
    
    // Return the organization with added properties
    return {
      ...org,
      owner,
      collaborators,
      fileCount,
      storage
    };
  });
};
